// ─── What a media url IS, client side (CYBORG-897 phase 4) ───────────────────
//
// The ONE definition of "our asset bucket" and "our permanent object url", shared
// by every place that used to carry its own copy of the same regex:
// render-markdown.ts's TRUSTED_IMAGE_HOST_RE (does this `![](…)` embed as an
// inline <img>?) and utils.ts's SHARED_ASSETS_URL_RE (does this avatar go through
// the relay thumbnailer?). Both comments already pointed at each other as "the
// same pin"; this makes that literally true, so widening one can no longer leave
// the other behind.
//
// PURE AND DEPENDENCY-FREE on purpose — no `$state`, no client import — so a
// plain `.ts` module (utils.ts, render-markdown.ts) can use it without pulling
// the runes module or the socket in. The media pass is a separate concern and
// lives in asset-url.svelte.ts.
//
// RESOLVE AT RENDER, NEVER REWRITE STORED DATA. Absolute S3 urls are already baked
// into messages.text (an agent's markdown images), timeline rows and page/TipTap
// documents. Migrating them would mean rewriting user content for a delivery
// detail, and the next delivery change would mean doing it again. Instead the
// sites that ALREADY had to recognize an asset url convert it on the way to the
// screen, and new writes mint the permanent form directly — so stored data
// converges with no migration and nothing to roll back.

/**
 * Our OWN asset buckets, pinned by exact name: a broad `*.s3.amazonaws.com` match would
 * let ANY attacker-controlled bucket embed an image. The server (asset-buckets.ts) and
 * iOS (AssetURLResolver.swift) carry the same list, pinned by asset-buckets.test.ts.
 */
const TRUSTED_ASSET_BUCKETS = ["cyborg7-shared-assets", "cyborg7-assets-155971443048"] as const;

/** `https://<bucket>.s3[.<region>|-<region>].amazonaws.com/<key>`; the names are [a-z0-9-] only. */
const SHARED_ASSETS_URL_RE = new RegExp(
  `^https://(?:${TRUSTED_ASSET_BUCKETS.join("|")})\\.s3(?:[.-][a-z0-9-]+)*\\.amazonaws\\.com/`,
  "i",
);

/** The CloudFront distribution in front of that bucket (a signed delivery url). */
const CLOUDFRONT_ASSETS_URL_RE = /^https:\/\/[a-z0-9-]+\.cloudfront\.net\//i;

/** The permanent form's path, exactly as media-delivery.ts's assetObjectPath mints it. */
const OBJECT_PATH_RE = /^\/api\/assets\/o\/(.+)$/;

/** True for a url on our own assets bucket (the historical stored form). */
export function isSharedAssetsUrl(url: string): boolean {
  return SHARED_ASSETS_URL_RE.test(url);
}

/**
 * The storage key inside one of OUR asset urls — the bucket url, its CloudFront
 * signed twin, or the permanent `/api/assets/o/<key>` form — or null when the url
 * is not ours. Query strings (a CloudFront signature, a media pass) are dropped:
 * the key is the path.
 */
export function assetKeyFromUrl(url: string): string | null {
  // Not on a bucket/CDN host ⇒ the only remaining form that can hold a key is the
  // permanent path itself. Anything else is not ours.
  if (!isSharedAssetsUrl(url) && !CLOUDFRONT_ASSETS_URL_RE.test(url)) return assetObjectKey(url);
  try {
    const key = decodeURIComponent(new URL(url).pathname.replace(/^\/+/, ""));
    return key || null;
  } catch {
    // A malformed url is not ours to rewrite — the caller renders it verbatim.
    // intentional: an unparseable url has no key; the caller keeps the original.
    return null;
  }
}

/**
 * The key inside a permanent object url, or null. Host-agnostic BY DESIGN — the
 * caller decides which origins it trusts (see isTrustedObjectUrl); this only reads
 * the shape.
 */
export function assetObjectKey(url: string): string | null {
  try {
    const parsed = new URL(url, "https://invalid.local");
    const m = OBJECT_PATH_RE.exec(parsed.pathname);
    if (!m) return null;
    return decodeURIComponent(m[1]) || null;
  } catch {
    // intentional: an unparseable url has no key; the caller keeps the original.
    return null;
  }
}

/** Build the permanent object url for a key against an absolute relay origin. */
export function assetObjectUrl(base: string, key: string): string {
  const encoded = key
    .split("/")
    .map((s) => encodeURIComponent(s))
    .join("/");
  return `${base.replace(/\/+$/, "")}/api/assets/o/${encoded}`;
}

/**
 * Convert any of OUR stored asset urls to the permanent form. Returns the input
 * unchanged when it is not ours (a `data:` uri, the bundled /agent-avatars preset
 * art, a self-host fs-backend url, someone else's host) — never invents an origin
 * for a url it does not recognize.
 *
 * `relayBase` MUST be absolute. On desktop the renderer origin is `cyborg://app`,
 * whose protocol handler serves local static files only, so a document-relative
 * `/api/...` silently returns index.html and the <img> fails to decode.
 */
export function toAssetObjectUrl(url: string, relayBase: string): string {
  if (!url || !relayBase) return url;
  if (assetObjectKey(url)) return url; // already permanent
  if (!isSharedAssetsUrl(url) && !CLOUDFRONT_ASSETS_URL_RE.test(url)) return url;
  const key = assetKeyFromUrl(url);
  return key ? assetObjectUrl(relayBase, key) : url;
}

/**
 * Is `url` a permanent object url on an origin we trust to embed inline?
 *
 * The host bound is the point. `![x](https://evil.example/api/assets/o/a/b.png)`
 * must NOT render as an inline <img> just because the PATH looks like ours — that
 * is the identical hole the bucket pin closes for `*.s3.amazonaws.com`. Trusted
 * origins are the build's cloud relay and the document's own origin (which is what
 * covers a self-host relay serving its own UI). A desktop shell pointed at a
 * self-host relay is deliberately NOT trusted here: the image degrades to a safe
 * link, which is a missing picture, not a hole.
 */
export function isTrustedObjectUrl(url: string, trustedOrigins: readonly string[]): boolean {
  if (!assetObjectKey(url)) return false;
  let origin: string;
  try {
    origin = new URL(url).origin;
  } catch {
    // intentional: a relative/malformed url has no origin to trust.
    return false;
  }
  return trustedOrigins.some((t) => {
    try {
      return new URL(t).origin === origin;
    } catch {
      // intentional: a misconfigured trusted base grants nothing.
      return false;
    }
  });
}

/** The key a share link may name: a bucket/CDN url's, or a permanent url's on a trusted origin. */
export function shareableAssetKey(
  url: string,
  trustedOrigins: readonly string[],
): string | undefined {
  const objectKey = assetObjectKey(url);
  if (objectKey) return isTrustedObjectUrl(url, trustedOrigins) ? objectKey : undefined;
  return assetKeyFromUrl(url) ?? undefined;
}

/** The app's landing page for a key (routes/media/[...key]): segments encoded, slashes kept. */
export function mediaSharePath(key: string): string {
  return `/media/${key
    .split("/")
    .map((s) => encodeURIComponent(s))
    .join("/")}`;
}
